import { useMemo, useState } from "react";
import api, { useList, errMsg, dayName } from "@/lib/api";
import { PageHeader, Spinner, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Calendar, Info } from "lucide-react";

const DAYS = [["Monday", 0], ["Tuesday", 1], ["Wednesday", 2], ["Thursday", 3],
              ["Friday", 4], ["Saturday", 5], ["Sunday", 6]];

const STATUS_STYLES = {
  needs_review: { label: "Needs Review", cls: "bg-amber-100 text-amber-900 border-amber-300" },
  active:        { label: "Active",       cls: "bg-emerald-100 text-emerald-900 border-emerald-300" },
  approved:      { label: "Approved",     cls: "bg-blue-100 text-blue-900 border-blue-300" },
  inactive:      { label: "Inactive",     cls: "bg-muted text-muted-foreground border-muted" },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.needs_review;
  return <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border ${s.cls}`}>{s.label}</span>;
}

export default function StudioReservationsPage() {
  const [statusFilter, setStatusFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [selected, setSelected] = useState(new Set());
  const { data: rows, loading, reload } = useList(
    `/admin/studio-reservations?${new URLSearchParams({
      ...(statusFilter && { status: statusFilter }),
      ...(sourceFilter && { source: sourceFilter }),
    }).toString()}`);
  const { data: teachers } = useList("/teachers");
  const { data: studios } = useList("/studios");
  const [editing, setEditing] = useState(null);  // row | "new" | null

  const grouped = useMemo(() => {
    const m = new Map();
    DAYS.forEach(([, d]) => m.set(d, []));
    (rows || []).forEach((r) => {
      if (!m.has(r.day_of_week)) m.set(r.day_of_week, []);
      m.get(r.day_of_week).push(r);
    });
    return m;
  }, [rows]);

  if (loading || !rows) return <Spinner full />;
  const toggleSel = (id) => {
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  };
  const bulkStatus = async (status) => {
    if (!selected.size) { toast.error("Select at least one row"); return; }
    if (!window.confirm(`Set ${selected.size} reservation(s) to "${status}"?`)) return;
    try {
      await api.post("/admin/studio-reservations/bulk-status",
                      { ids: [...selected], status });
      toast.success(`Updated ${selected.size} reservation(s)`);
      setSelected(new Set()); reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const bulkDelete = async () => {
    if (!selected.size) { toast.error("Select at least one row"); return; }
    if (!window.confirm(`Delete ${selected.size} reservation(s)? This cannot be undone.`)) return;
    try {
      await api.post("/admin/studio-reservations/bulk-delete", { ids: [...selected] });
      toast.success(`Deleted ${selected.size} reservation(s)`);
      setSelected(new Set()); reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (id, label) => {
    if (!window.confirm(`Delete reservation for ${label}?`)) return;
    try {
      await api.delete(`/admin/studio-reservations/${id}`);
      toast.success("Reservation deleted");
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="studio-reservations-page">
      <PageHeader title="Studio Reservations"
        description="Imported CSV reservations start as 'Needs Review' — they don't block class enrolment until you mark them Active or Approved. Confirmed classes always reserve their own time independently."
        actions={
          <Button onClick={() => setEditing("new")} data-testid="new-reservation-btn">
            <Plus className="h-4 w-4 mr-1" />New reservation
          </Button>} />

      {/* Filter + bulk action toolbar */}
      <div className="rounded-md border bg-card p-3 mb-4 flex flex-wrap items-center gap-3" data-testid="reservation-toolbar">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                className="rounded-md border px-2 py-1.5 bg-background text-sm" data-testid="filter-status">
          <option value="">All statuses</option>
          <option value="needs_review">Needs Review</option>
          <option value="active">Active</option>
          <option value="approved">Approved</option>
          <option value="inactive">Inactive</option>
        </select>
        <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}
                className="rounded-md border px-2 py-1.5 bg-background text-sm" data-testid="filter-source">
          <option value="">All sources</option>
          <option value="csv_import">CSV Import</option>
          <option value="manual">Manual</option>
        </select>
        <div className="flex-1" />
        {selected.size > 0 && (
          <>
            <span className="text-xs text-muted-foreground">{selected.size} selected</span>
            <Button size="sm" variant="outline" onClick={() => bulkStatus("approved")} data-testid="bulk-approve-btn">Approve</Button>
            <Button size="sm" variant="outline" onClick={() => bulkStatus("active")} data-testid="bulk-activate-btn">Activate</Button>
            <Button size="sm" variant="outline" onClick={() => bulkStatus("inactive")} data-testid="bulk-deactivate-btn">Deactivate</Button>
            <Button size="sm" variant="outline" className="text-destructive border-destructive/40" onClick={bulkDelete} data-testid="bulk-delete-btn">Delete</Button>
          </>
        )}
      </div>

      <div className="space-y-4" data-testid="reservations-by-day">
        {DAYS.map(([name, dow]) => {
          const list = grouped.get(dow) || [];
          return (
            <div key={dow} className="rounded-lg border bg-card p-4" data-testid={`day-${name}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-secondary flex items-center gap-2">
                  <Calendar className="h-4 w-4" />{name}
                </h3>
                <span className="text-xs text-muted-foreground bg-muted rounded-full px-2 py-0.5">{list.length}</span>
              </div>
              {list.length === 0 ? <EmptyState text={`No reservations on ${name}.`} small /> : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {list.map((r) => (
                    <div key={r.id} className="rounded-md border border-amber-300 border-dashed bg-amber-50/30 p-3 flex items-start gap-2"
                         data-testid={`reservation-row-${r.id}`}>
                      <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleSel(r.id)}
                              className="mt-1 rounded" data-testid={`select-${r.id}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="text-sm font-medium truncate">{r.teacher_name}</p>
                          <StatusBadge status={r.reservation_status || "needs_review"} />
                          <span className="text-[9px] uppercase tracking-wider text-muted-foreground">
                            {r.source === "manual" ? "manual" : "csv"}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {r.studio_name} · {r.start_time}–{r.end_time}
                          {r.instruments && <> · {r.instruments}</>}
                        </p>
                        {r.note && <p className="text-[10px] text-muted-foreground mt-1 italic">{r.note}</p>}
                      </div>
                      <button onClick={() => setEditing(r)} className="text-muted-foreground hover:text-foreground p-1" data-testid={`edit-reservation-${r.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => remove(r.id, `${r.teacher_name} ${r.start_time}`)}
                              className="text-muted-foreground hover:text-destructive p-1" data-testid={`delete-reservation-${r.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <ReservationDialog
        open={!!editing}
        row={editing === "new" ? null : editing}
        teachers={teachers || []}
        studios={studios || []}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); reload(); }} />
    </div>
  );
}


function ReservationDialog({ open, row, teachers, studios, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useMemo(() => {
    setForm(row || {
      teacher_id: "", studio_id: "", day_of_week: 2,
      start_time: "16:30", end_time: "19:30",
      instruments: "", note: "",
    });
  }, [row, open]);

  if (!open) return null;
  const save = async () => {
    if (!form.teacher_id || !form.studio_id) { toast.error("Pick teacher + studio"); return; }
    if (form.start_time >= form.end_time) { toast.error("End time must be after start"); return; }
    setBusy(true);
    try {
      const payload = {
        teacher_id: form.teacher_id, studio_id: form.studio_id,
        day_of_week: parseInt(form.day_of_week),
        start_time: form.start_time, end_time: form.end_time,
        instruments: form.instruments || null, note: form.note || null,
      };
      if (row?.id) {
        await api.put(`/admin/studio-reservations/${row.id}`, payload);
      } else {
        await api.post("/admin/studio-reservations", payload);
      }
      toast.success(row?.id ? "Reservation updated" : "Reservation created");
      onSaved();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.conflicts) {
        toast.error(`Conflict: ${detail.conflicts.length} overlapping booking(s) in that studio`);
      } else {
        toast.error(errMsg(e));
      }
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="reservation-dialog">
        <DialogHeader>
          <DialogTitle>{row?.id ? "Edit" : "New"} studio reservation</DialogTitle>
          <DialogDescription>Hold a studio for a teacher in a weekly recurring slot. Will block other teachers + trial bookings from using the room at the same time.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Music Instructor</Label>
              <select value={form.teacher_id || ""} onChange={(e) => setForm({ ...form, teacher_id: e.target.value })}
                      className="w-full rounded-md border px-3 py-2 bg-background text-sm" data-testid="dialog-teacher-select">
                <option value="">— Pick —</option>
                {teachers.filter((t) => t.status !== "deleted").map((t) => <option key={t.id} value={t.id}>{t.full_name}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Studio</Label>
              <select value={form.studio_id || ""} onChange={(e) => setForm({ ...form, studio_id: e.target.value })}
                      className="w-full rounded-md border px-3 py-2 bg-background text-sm" data-testid="dialog-studio-select">
                <option value="">— Pick —</option>
                {studios.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Day of week</Label>
            <select value={form.day_of_week} onChange={(e) => setForm({ ...form, day_of_week: e.target.value })}
                    className="w-full rounded-md border px-3 py-2 bg-background text-sm" data-testid="dialog-day-select">
              {DAYS.map(([name, d]) => <option key={d} value={d}>{name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Start time</Label>
              <Input type="time" value={form.start_time || ""} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="dialog-start-time" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">End time</Label>
              <Input type="time" value={form.end_time || ""} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="dialog-end-time" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Instruments / label (optional)</Label>
            <Input value={form.instruments || ""} onChange={(e) => setForm({ ...form, instruments: e.target.value })}
                    placeholder='e.g. "Drum"' data-testid="dialog-instruments" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Internal note (optional)</Label>
            <Input value={form.note || ""} onChange={(e) => setForm({ ...form, note: e.target.value })}
                    placeholder="Why is this room held?" data-testid="dialog-note" />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="save-reservation-btn">{busy ? "Saving…" : "Save"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
